import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ShieldCheck, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { setAgeGateShow } from "../lib/ageGateBus";
import { showUpgradeExternally } from "./UpgradePrompt";
import { useAuth } from "../context/AuthContext";
import { usePlayer } from "../context/PlayerContext";

// 18+ age check for explicit songs (AgeVerif).
//   - A guest who clicks an explicit song gets the same "Create a free login" prompt as any locked song.
//   - A logged-in member who has not passed the check gets this pop-up with a "Verify with AgeVerif" button.
//   - After they pass (the server confirms it with AgeVerif, see /api/age/* in backend/server.py) the account is
//     marked verified, the page reloads once so every list gets the unlocked links, and the song they clicked plays.
//     From then on explicit songs play like any other song.

const RESUME_KEY = "dtm_age_resume";        // "songId|time": the song to start once the page has reloaded after a pass
const RESUME_MAX_MS = 30 * 60 * 1000;
const POLL_MS = 2500;                        // while the AgeVerif window is open, ask the server if the account is verified yet
const POLL_MAX_MS = 15 * 60 * 1000;
const WINDOW_NAME = "ageverif-check";

export const isAdult = (user) => !!user && (user.role === "admin" || user.age_status === "verified");

const rememberSong = (song) => {
  try { if (song && song.id) sessionStorage.setItem(RESUME_KEY, `${song.id}|${Date.now()}`); } catch { /* ignore */ }
};

export function AgeGateProvider({ children }) {
  const { user, ready } = useAuth();
  const { playOne } = usePlayer();
  const location = useLocation();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [song, setSong] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState("");
  const userRef = useRef(user);
  const popupRef = useRef(null);
  const pollRef = useRef(null);
  const songRef = useRef(null);
  useEffect(() => { userRef.current = user; }, [user]);
  useEffect(() => { songRef.current = song; }, [song]);

  const stopWatching = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const closeBox = useCallback(() => {
    stopWatching();
    setOpen(false);
    setWaiting(false);
    setError("");
  }, [stopWatching]);

  // The account passed: close the window, remember the song, reload once so every list gets the unlocked links.
  const finish = useCallback(() => {
    stopWatching();
    try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch { /* ignore */ }
    rememberSong(songRef.current);
    window.location.reload();
  }, [stopWatching]);

  // Ask the server whether the account is verified yet. The server only says yes after AgeVerif confirmed it.
  const checkNow = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (isAdult(data)) finish();
    } catch { /* ignore, the next check tries again */ }
  }, [finish]);

  const show = useCallback((video) => {
    // A guest (or anyone not logged in) gets the same prompt every locked song gives.
    if (!userRef.current) {
      showUpgradeExternally("Create a free login", {
        ctaLabel: "Create a free login",
        ctaPath: "/register",
        body: "Sign up to keep watching full tracks, build playlists, and save favorites.",
      });
      return;
    }
    setSong(video || null);
    setError("");
    setWaiting(false);
    setOpen(true);
  }, []);

  useEffect(() => { setAgeGateShow(show); return () => setAgeGateShow(null); }, [show]);

  // The AgeVerif window tells this page when it is done, and the server is asked every few seconds as a backup
  // (some browsers cut the link between the two windows).
  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === "dtm-age-check") {
        if (e.data.ok) finish(); else { stopWatching(); setWaiting(false); checkNow(); }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [finish, stopWatching, checkNow]);

  useEffect(() => stopWatching, [stopWatching]);

  const start = async () => {
    setError("");
    setWaiting(true);
    // The window is opened right away, inside the click, so pop-up blockers allow it. Its address is filled in once the server answers.
    let win = null;
    try { win = window.open("", WINDOW_NAME, "width=480,height=780"); } catch { win = null; }
    popupRef.current = win;
    try {
      const { data } = await api.post("/age/start", { return_to: song ? song.id : null, mode: win ? "popup" : "tab" });
      if (win && !win.closed) {
        win.location.href = data.url;
        stopWatching();
        const began = Date.now();
        pollRef.current = setInterval(() => {
          if (Date.now() - began > POLL_MAX_MS) { stopWatching(); setWaiting(false); return; }
          checkNow();
        }, POLL_MS);
      } else {
        // Pop-ups are blocked: do the check in this tab. The server brings the person back to this song afterwards.
        rememberSong(song);
        window.location.href = data.url;
      }
    } catch (err) {
      try { if (win) win.close(); } catch { /* ignore */ }
      setWaiting(false);
      const d = err?.response?.data?.detail;
      if (d === "age_already_verified") { finish(); return; }
      if (d === "age_check_not_set_up") setError("Age checks are not available on this site yet. Please try again later.");
      else setError(typeof d === "string" ? d : "Something went wrong starting the age check. Please try again.");
    }
  };

  const startAgain = () => {
    stopWatching();
    try { if (popupRef.current && !popupRef.current.closed) popupRef.current.close(); } catch { /* ignore */ }
    setWaiting(false);
  };

  // After a pass the page reloads: start the song they clicked.
  useEffect(() => {
    if (!ready || !isAdult(user)) return;
    let raw = null;
    try { raw = sessionStorage.getItem(RESUME_KEY); sessionStorage.removeItem(RESUME_KEY); } catch { /* ignore */ }
    if (!raw) return;
    const [id, at] = raw.split("|");
    if (!id || Date.now() - Number(at || 0) > RESUME_MAX_MS) return;
    api.get(`/videos/${id}`).then(({ data }) => { if (data && !data.age_locked) playOne(data); }).catch(() => {});
  }, [ready, user, playOne]);

  // Back from the check in the same tab (pop-ups blocked): the address ends in ?age=ok or ?age=no.
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const result = q.get("age");
    if (!result) return;
    q.delete("age");
    nav({ pathname: location.pathname, search: q.toString() ? `?${q.toString()}` : "" }, { replace: true, state: location.state });
    if (result === "ok") toast.success("You're verified. Explicit songs are unlocked.");
    else toast.error("The age check was not finished, so explicit songs are still locked.");
  }, [location.pathname, location.search, location.state, nav]);

  return (
    <>
      {children}
      {open && (
        <div data-testid="age-gate" className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={closeBox}>
          <div className="relative w-full max-w-md rounded-2xl border border-rose-400/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-rose-500/5 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button data-testid="age-gate-close" onClick={closeBox} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 text-zinc-400" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-2 text-rose-300 mb-3">
              <ShieldCheck className="w-5 h-5" />
              <span className="font-mono-ui text-xs uppercase tracking-widest">18+ only</span>
            </div>
            <h2 className="font-display text-2xl font-extrabold mb-2" data-testid="age-gate-title">
              {song ? "This song is explicit" : "Explicit songs are for 18 and older"}
            </h2>
            <p className="text-sm text-zinc-400 mb-2">
              Confirm your age once with AgeVerif and every explicit song unlocks on your account, with no more checks.
            </p>
            <p className="text-sm text-zinc-500 mb-5">
              AgeVerif does the selfie or ID check. We never see it, we only save that you passed. If you are under 18, explicit songs stay locked.
            </p>
            {error && <p data-testid="age-gate-error" className="text-sm text-rose-300 mb-4">{error}</p>}
            {waiting ? (
              <div className="mb-1" data-testid="age-gate-waiting">
                <p className="text-sm text-zinc-300 mb-3">Finish the check in the AgeVerif window. This unlocks by itself when you pass.</p>
                <button onClick={startAgain} className="text-xs text-zinc-400 underline hover:text-zinc-200">Closed the window? Start again</button>
              </div>
            ) : (
              <div className="flex gap-2 justify-end">
                <button data-testid="age-gate-not-now" onClick={closeBox} className="px-4 py-2 rounded-full border border-white/15 text-sm hover:bg-white/5">Not now</button>
                <button data-testid="age-gate-verify" onClick={start}
                  className="px-4 py-2 rounded-full bg-[#004db3] text-white text-sm font-semibold hover:bg-[#0a5fd1] inline-flex items-center gap-2">
                  {/* AgeVerif asks for its logo on this button. Put the file in frontend/public/ as ageverif-white.svg (see the install steps). */}
                  <img src="/ageverif-white.svg" alt="" className="h-4 w-auto" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  Verify with AgeVerif
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
